/**
 * AdminArticleEditor — lightweight in-browser markdown editor for
 * super-admin edits to article content.
 *
 * Route:   /admin/articles/edit/:slug
 * Auth:    is_admin required (redirects otherwise)
 * Reads:   GET  /api/articles/{slug}
 * Writes:  PATCH /api/articles/{slug}  (auto-renders body → content_html
 *          and mirrors into sp_articles which the reader page uses)
 *
 * UX: left = markdown textarea, right = live HTML preview. Save button
 * top-right. Autosave-on-blur is intentionally NOT wired — editorial
 * writing is deliberate, not a Google Docs stream. Manual save only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Minimal markdown → HTML preview (backend does the real render on save).
function mdToHtml(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Headings
  html = html.replace(/^###### (.*)$/gm, "<h6>$1</h6>")
             .replace(/^##### (.*)$/gm, "<h5>$1</h5>")
             .replace(/^#### (.*)$/gm, "<h4>$1</h4>")
             .replace(/^### (.*)$/gm, "<h3>$1</h3>")
             .replace(/^## (.*)$/gm, "<h2>$1</h2>")
             .replace(/^# (.*)$/gm, "<h1>$1</h1>");
  // Blockquote
  html = html.replace(/^&gt; (.*)$/gm, "<blockquote>$1</blockquote>");
  // Lists (simple)
  html = html.replace(/^[-*] (.*)$/gm, "<li>$1</li>");
  // Bold + italic + inline code
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
             .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
             .replace(/_([^_]+)_/g, "<em>$1</em>")
             .replace(/`([^`]+)`/g, "<code>$1</code>");
  // Links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  // Horizontal rule
  html = html.replace(/^---$/gm, "<hr/>");
  // Paragraphs
  html = html
    .split(/\n\n+/)
    .map((p) => (p.match(/^<(h\d|blockquote|li|hr)/) ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`))
    .join("\n");
  return html;
}

export default function AdminArticleEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Editable fields
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [kicker, setKicker] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [published, setPublished] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const isAdmin = user?.is_admin;

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      toast.error("Admin access required");
      nav("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await http.get(`/api/articles/${encodeURIComponent(slug)}`);
        setArticle(data);
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
        setKicker(data.kicker || "");
        setExcerpt(data.excerpt || "");
        setBody(data.body || "");
        setPublished(data.published !== false);
      } catch (e) {
        toast.error(`Article not found: ${slug}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isAdmin, authLoading, nav]);

  const previewHtml = useMemo(() => mdToHtml(body), [body]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await http.patch(`/api/articles/${encodeURIComponent(slug)}`, {
        title, subtitle, kicker, excerpt, body, published,
      });
      setSavedAt(new Date());
      toast.success("Saved. Reader page will pick up on next refresh.");
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  }, [slug, title, subtitle, kicker, excerpt, body, published]);

  // Keyboard shortcut Cmd/Ctrl+S
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [save]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50" data-testid="editor-loading">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-article-editor">
      <Helmet><title>Editing — {title || slug}</title></Helmet>

      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/admin/articles" className="text-neutral-500 hover:text-neutral-900 flex items-center gap-1" data-testid="editor-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex-1 flex items-center gap-2 text-sm">
            <span className="text-neutral-400">Editing article:</span>
            <code className="text-xs bg-neutral-100 px-2 py-0.5 rounded">{slug}</code>
            {savedAt && (
              <span className="text-emerald-600 text-xs flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                saved {savedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              data-testid="editor-published-toggle"
            />
            Published
          </label>
          <button
            onClick={() => setShowPreview((s) => !s)}
            className="text-sm px-3 py-1.5 rounded-md border border-neutral-300 hover:bg-neutral-50 flex items-center gap-1.5"
            data-testid="editor-preview-toggle"
          >
            <Eye className="w-4 h-4" /> {showPreview ? "Hide" : "Show"} preview
          </button>
          <a
            href={`/articles/${slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm px-3 py-1.5 rounded-md border border-neutral-300 hover:bg-neutral-50 flex items-center gap-1.5"
            data-testid="editor-open-live-btn"
          >
            <ExternalLink className="w-4 h-4" /> Open live
          </a>
          <button
            onClick={save}
            disabled={saving}
            className="text-sm px-4 py-1.5 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="editor-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save (⌘S)
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="max-w-6xl mx-auto px-6 py-6 grid gap-3">
        <input
          value={kicker}
          onChange={(e) => setKicker(e.target.value)}
          placeholder="Kicker (small label above title, e.g. 'Family Feature')"
          className="w-full px-3 py-2 text-xs uppercase tracking-widest text-neutral-600 border border-neutral-200 rounded-md bg-white"
          data-testid="editor-kicker"
        />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Article title"
          className="w-full px-3 py-2 text-2xl font-serif font-semibold border border-neutral-200 rounded-md bg-white"
          data-testid="editor-title"
        />
        <input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Subtitle"
          className="w-full px-3 py-2 text-base text-neutral-700 border border-neutral-200 rounded-md bg-white"
          data-testid="editor-subtitle"
        />
        <textarea
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="Excerpt (~2 sentences for card previews and SEO)"
          rows={2}
          className="w-full px-3 py-2 text-sm text-neutral-600 border border-neutral-200 rounded-md bg-white resize-y"
          data-testid="editor-excerpt"
        />
      </div>

      {/* Body editor + preview */}
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className={`grid gap-6 ${showPreview ? "lg:grid-cols-2" : ""}`}>
          <div>
            <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">
              Body (Markdown)
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={30}
              className="w-full px-4 py-3 font-mono text-sm border border-neutral-200 rounded-md bg-white resize-y leading-relaxed"
              style={{ minHeight: "70vh" }}
              data-testid="editor-body"
              spellCheck
            />
            <p className="text-xs text-neutral-500 mt-2">
              {body.length.toLocaleString()} characters · ~
              {Math.max(1, Math.round(body.split(/\s+/).length / 220))} min read
            </p>
          </div>
          {showPreview && (
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">
                Preview
              </label>
              <div
                className="prose prose-neutral max-w-none bg-white border border-neutral-200 rounded-md p-6 leading-relaxed"
                style={{ minHeight: "70vh" }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
                data-testid="editor-preview"
              />
              <p className="text-xs text-neutral-500 mt-2 italic">
                This is a quick client-side preview — the final rendered HTML on the reader page uses the full Python markdown parser and may look slightly different.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
